import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { TFunction } from 'i18next';
import { Dispatch } from 'redux';
import { RootState } from 'app/AppStore';
import { DataContainer } from 'components/DataContainer';
import Filter from 'components/Filter';
import { RangeFacetLocal, TextFacetLocal } from 'components/Filter/Filter.types';
import {
  getIamCatalogFilters,
  resetIamRangeFilters,
  resetIamTextFilters,
  setIamRangeFilter,
  setIamTextFilter,
} from 'domains/catalog/Catalog.store';
import {
  IAMLaborTimesNodeWrapper,
  IAMReferencesWrapper,
  OEM_BRAND_KEY,
  OTHER_BRAND_KEY,
} from 'domains/catalog/Catalog.types';
import { getPricesMap } from 'domains/references';
import { SparePartsViewType } from 'domains/user';
import ReferenceCardsContainer, {
  getIamContainerLoader,
} from 'pages/CatalogPage/IAM/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCardsContainer';
import {
  BrandTabLayout,
  ColumnsSpacer,
  FilterColumn,
  ResultsColumn,
  ToggleWrapper,
  TopMarginWrapper,
} from 'pages/CatalogPage/IAM/SubcategorySection/SparePartsSection/SparePartsBrandTab.styled';
import { PricelessFilterToggle } from 'pages/UniversalProductsPage/ProductsPage/PricelessFilterToggle';
import { ERROR, getData, hasData, hasPrice, isLoading, LOADING, NO_DATA, NOT_FOUND, useLarge } from 'utils';
import { usePricelessFilter } from 'utils/hooks/usePricelessFilter';

interface SparePartsBrandTabProps {
  sparePartsView: SparePartsViewType;
  referencesWrapper: IAMReferencesWrapper;
  nodeId?: string;
  laborTimesWrapper: NO_DATA | IAMLaborTimesNodeWrapper;
  setNumOfProducts: (num: number) => void;
  isCompatible: boolean;
  filterType: typeof OEM_BRAND_KEY | typeof OTHER_BRAND_KEY;
  facetsMapBuilder: (t: TFunction) => Map<string, { label: string; ids: string[] }>;
  leftTopContent?: ReactNode;
  withTopMargin?: boolean;
}

const resetBrandFilters = (dispatch: Dispatch, filterType: typeof OEM_BRAND_KEY | typeof OTHER_BRAND_KEY) => {
  dispatch(resetIamTextFilters({ filterType }));
  dispatch(resetIamRangeFilters({ filterType }));
};

const SparePartsBrandTab = ({
  sparePartsView,
  referencesWrapper,
  nodeId,
  laborTimesWrapper,
  setNumOfProducts,
  isCompatible,
  filterType,
  facetsMapBuilder,
  leftTopContent,
  withTopMargin,
}: SparePartsBrandTabProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isPricelessFilterEnabled = filterType === OTHER_BRAND_KEY;
  const referencesWrapperData = getData(referencesWrapper);
  const filters = useSelector(getIamCatalogFilters);
  const selectedFilters = filterType === OEM_BRAND_KEY ? filters.oemFilters : filters.otherBrandFilters;
  const large = useLarge();
  const [textFilters, setTextFilters] = useState<TextFacetLocal[]>(referencesWrapperData?.textFilters ?? []);
  const [rangeFilters, setRangeFilters] = useState<RangeFacetLocal[]>(referencesWrapperData?.rangeFilters ?? []);
  const [showPriceless, setShowPriceless] = useState(false);
  const [pricelessScopeReferenceNumbers, setPricelessScopeReferenceNumbers] = useState<string[]>([]);
  const referenceNumbers = referencesWrapperData?.referenceNumbers ?? [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pricelessReferenceNumbers = isPricelessFilterEnabled ? pricelessScopeReferenceNumbers : [];
  const prices = useSelector((state: RootState) => getPricesMap(state, pricelessReferenceNumbers));
  const priceItems = useMemo(() => {
    return pricelessReferenceNumbers.map((referenceNumber) => {
      const price = prices.get(referenceNumber);
      return {
        id: referenceNumber,
        hasPriceData: (hasData(price) && hasPrice(sparePartsView, getData(price))) || isLoading(price),
      };
    });
  }, [prices, pricelessReferenceNumbers, sparePartsView]);

  const { pricelessCount, showToggle, shouldIncludePriceless } = usePricelessFilter(
    isPricelessFilterEnabled ? priceItems : [],
    isPricelessFilterEnabled && showPriceless,
  );

  useEffect(() => {
    setTextFilters(referencesWrapperData?.textFilters ?? []);
  }, [referencesWrapperData?.textFilters, nodeId]);

  useEffect(() => {
    setRangeFilters(referencesWrapperData?.rangeFilters ?? []);
  }, [referencesWrapperData?.rangeFilters, nodeId]);

  useEffect(() => {
    setShowPriceless(false);
  }, [nodeId, filterType]);

  useEffect(() => {
    resetBrandFilters(dispatch, filterType);
    if (referencesWrapper === NOT_FOUND || referencesWrapper === ERROR) {
      setNumOfProducts(0);
    }
  }, [dispatch, filterType, referencesWrapper, setNumOfProducts]);

  const content = (
    <BrandTabLayout direction={large ? 'row' : 'column'}>
      <FilterColumn size={0}>
        {leftTopContent}
        <Filter
          filters={selectedFilters}
          textFilters={textFilters}
          rangeFilters={rangeFilters}
          setTextFilters={(id, item) => dispatch(setIamTextFilter({ id, item, filterType }))}
          setRangeFilters={(id, range) => dispatch(setIamRangeFilter({ id, range, filterType }))}
          resetFilters={() => resetBrandFilters(dispatch, filterType)}
          loading={referencesWrapper === LOADING}
          facetsMap={facetsMapBuilder(t)}
        />
      </FilterColumn>
      <ColumnsSpacer $large={large} />
      <ResultsColumn size={1}>
        <DataContainer data={referencesWrapper} Loading={getIamContainerLoader}>
          <ReferenceCardsContainer
            isCompatible={isCompatible}
            includePriceless={isPricelessFilterEnabled && shouldIncludePriceless}
            enablePricelessFilter={isPricelessFilterEnabled}
            onPricelessScopeChange={isPricelessFilterEnabled ? setPricelessScopeReferenceNumbers : undefined}
            sparePartsView={sparePartsView}
            referenceNumbers={referenceNumbers}
            nodeId={nodeId}
            laborTimesWrapper={laborTimesWrapper}
            setNumOfProducts={setNumOfProducts}
            textFilters={textFilters}
            rangeFilters={rangeFilters}
            setTextFilters={setTextFilters}
            setRangeFilters={setRangeFilters}
          />
        </DataContainer>
        {isPricelessFilterEnabled && showToggle && (
          <ToggleWrapper>
            <PricelessFilterToggle
              showPriceless={showPriceless}
              pricelessCount={pricelessCount}
              onToggle={() => setShowPriceless((prev) => !prev)}
            />
          </ToggleWrapper>
        )}
      </ResultsColumn>
    </BrandTabLayout>
  );

  return withTopMargin ? <TopMarginWrapper>{content}</TopMarginWrapper> : content;
};

export default SparePartsBrandTab;
